import { GpmPayConfigError } from '../core/errors.js';
import type { BankAccount, Bank } from '../types/bank.js';
import { crc16ccitt } from './crc16.js';

/** VietQR truncates the addendum field; the backend applies the same limit. */
const DESCRIPTION_MAX_LENGTH = 25;

export interface VietQrInput {
  /** NAPAS BIN of the beneficiary bank (`bank.bin`). */
  bankBin: string;
  accountNumber: string;
  /** Omit for a static QR the payer fills in themselves. */
  amount?: number | string;
  /** Truncated to 25 chars, matching the server. */
  description?: string;
  serviceCode?: 'QRIBFTTA' | 'QRIBFTTC';
}

export interface VietQrImageInput extends VietQrInput {
  template?: 'compact' | 'compact2' | 'qr_only' | 'print';
  accountName?: string;
}

const tlv = (id: string, value: string): string =>
  id + value.length.toString().padStart(2, '0') + value;

function isDynamic(amount: number | string | undefined): boolean {
  return amount !== undefined && amount !== null && amount !== '';
}

function trimDescription(description: string | undefined): string | undefined {
  return description === undefined
    ? undefined
    : description.slice(0, DESCRIPTION_MAX_LENGTH);
}

/**
 * Build an EMVCo/VietQR payload string.
 *
 * @remarks
 * This is the main entry point when you handle reconciliation yourself — the
 * common case. Put your own order code in `description`; it becomes the
 * transfer content, and you match it against `event.payload.content` when the
 * webhook arrives.
 *
 * ```ts
 * const payload = buildVietQrPayload({
 *   bankBin: '970422',          // account.bank.bin
 *   accountNumber: '1234567890',
 *   amount: 50_000,
 *   description: 'DH123',       // your own code — keep it <= 25 chars
 * });
 * ```
 *
 * Also fine for a static account QR: omit `amount` and the payer fills it in.
 *
 * `description` is what the payer ends up transferring, so it carries your
 * reconciliation code. VietQR truncates it to 25 characters and banks may
 * prepend their own prefix — keep the code short and at the front. For the
 * common case, {@link buildPaymentInstructions} wraps this up from a bank
 * account object.
 *
 * Byte-for-byte port of `apps/backend/src/modules/vietqr/vietqr.util.ts`.
 */
export function buildVietQrPayload(input: VietQrInput): string {
  const {
    bankBin,
    accountNumber,
    amount,
    description,
    serviceCode = 'QRIBFTTA',
  } = input;

  const beneficiaryOrg = tlv('00', bankBin) + tlv('01', accountNumber);
  const merchantAccountInfo =
    tlv('00', 'A000000727') + tlv('01', beneficiaryOrg) + tlv('02', serviceCode);

  const dynamic = isDynamic(amount);
  const trimmedDesc = trimDescription(description);
  const additionalData = trimmedDesc ? tlv('08', trimmedDesc) : '';

  const parts = [
    tlv('00', '01'),
    tlv('01', dynamic ? '12' : '11'),
    tlv('38', merchantAccountInfo),
    tlv('53', '704'),
    ...(dynamic ? [tlv('54', String(amount))] : []),
    tlv('58', 'VN'),
    ...(additionalData ? [tlv('62', additionalData)] : []),
  ];

  const base = parts.join('') + '6304';
  return base + crc16ccitt(base);
}

/**
 * Build an `img.vietqr.io` image URL.
 *
 * The server always emits the `compact` template; pass `template` to pick a
 * different one for your own checkout page.
 */
export function buildVietQrImageUrl(input: VietQrImageInput): string {
  const { bankBin, accountNumber, amount, template = 'compact' } = input;
  const trimmedDesc = trimDescription(input.description);

  const url = new URL(
    `https://img.vietqr.io/image/${bankBin}-${accountNumber}-${template}.png`,
  );
  if (isDynamic(amount)) url.searchParams.set('amount', String(amount));
  if (trimmedDesc) url.searchParams.set('addInfo', trimmedDesc);
  if (input.accountName !== undefined) {
    url.searchParams.set('accountName', input.accountName);
  }

  return url.toString();
}

export interface PaymentRequest {
  /**
   * The destination account, as returned by
   * `client.bankAccounts.retrieve(id)`. Must carry its `bank` relation — the
   * NAPAS BIN lives there.
   */
  bankAccount: BankAccount & { bank?: Bank };
  /** VND. A number, or the string form the API returns for `Decimal`. */
  amount: number | string;
  /**
   * The transfer content you will look for later. Put **your own** order
   * reference in here — GPM Pay mints nothing and matches nothing. Truncated
   * to 25 chars inside the QR, so keep the code short and near the front.
   */
  transferContent: string;
  serviceCode?: 'QRIBFTTA' | 'QRIBFTTC';
  template?: VietQrImageInput['template'];
}

export interface PaymentInstructions {
  /** EMVCo payload. Render this as the QR. */
  qrPayload: string;
  qrImageUrl: string;
  amount: number;
  /** What the payer MUST put in the transfer content. */
  transferContent: string;
  bankName: string | undefined;
  bankBin: string | undefined;
  accountNumber: string;
  accountName: string;
}

/**
 * Everything a checkout page needs, built entirely client-side.
 *
 * @remarks
 * There is no server-side order to read this from — GPM Pay has no
 * order/QR-minting endpoint. You choose the reference code, you render the QR,
 * and you reconcile it yourself when the transaction webhook arrives by looking
 * for that code in `payload.content`.
 *
 * @throws {GpmPayConfigError} when `bankAccount.bank.bin` is missing — without
 * the BIN there is no valid VietQR payload to build.
 *
 * @example
 * const account = await client.bankAccounts.retrieve(bankAccountId);
 * const ref = `SHOP${orderId}`;
 * const instructions = buildPaymentInstructions({
 *   bankAccount: account,
 *   amount: 250_000,
 *   transferContent: ref,
 * });
 */
export function buildPaymentInstructions(
  request: PaymentRequest,
): PaymentInstructions {
  const account = request.bankAccount;
  const bank = account.bank;

  if (bank === undefined || bank.bin === '') {
    throw new GpmPayConfigError(
      'buildPaymentInstructions: bankAccount.bank.bin is required to build a ' +
        'VietQR payload. Fetch the account with its `bank` relation, e.g. ' +
        '`await client.bankAccounts.retrieve(id)`.',
    );
  }
  const bankBin = bank.bin;

  const base: VietQrInput = {
    bankBin,
    accountNumber: account.accountNumber,
    amount: request.amount,
    description: request.transferContent,
  };
  if (request.serviceCode !== undefined) base.serviceCode = request.serviceCode;

  const imageInput: VietQrImageInput = {
    ...base,
    accountName: account.ownerName,
  };
  if (request.template !== undefined) imageInput.template = request.template;

  return {
    qrPayload: buildVietQrPayload(base),
    qrImageUrl: buildVietQrImageUrl(imageInput),
    amount: Number(request.amount),
    transferContent: request.transferContent,
    bankName: bank.shortName === '' ? bank.name : bank.shortName,
    bankBin,
    accountNumber: account.accountNumber,
    accountName: account.ownerName,
  };
}
